import {DateStringNullable, Dictionary, NullableKeys, todayDate} from '@fl/cmsch-fe-library';

/** Form name used for breeder and organization facility edits. */
export const subjectFacilityForm = 'subjectFacilityForm';

/** Values submitted by the facility relation form. */
export interface SubjectFacilityFormValues {
    /** Approval start date. */
    approvedFrom: DateStringNullable;
    /** Approval end date. */
    approvedTo: DateStringNullable;
    /** Selected center ids. */
    centerIds: Array<number>;
    /** Selected farm ids. */
    farmIds: Array<number>;
    /** Selected subject id. */
    subjectId: number;
    /** Selected role ids. */
    roleIds: Array<number>;
    /** Owning user profile id. */
    userProfileId: number;
    /** Role ids that belong to the current user profile. */
    userProfileRoleIds: Array<number>;
}

/**
 * Empty form values used before the facility form is initialized.
 *
 * @param userProfileId User profile that owns the facility row.
 * @returns Default facility form values for a new row.
 *
 * @example
 * subjectFacilityFormInitialValues(42);
 */
export const subjectFacilityFormInitialValues = (userProfileId: number): NullableKeys<SubjectFacilityFormValues> => ({
    approvedFrom: todayDate(),
    approvedTo: null,
    centerIds: null,
    farmIds: null,
    subjectId: null,
    roleIds: [],
    userProfileId,
    userProfileRoleIds: [],
});

/** Initial data extracted from an existing breeder or organization row. */
export interface SubjectFacilityFormEntryData {
    /** Approval start date. */
    approvedFrom: DateStringNullable;
    /** Approval end date. */
    approvedTo: DateStringNullable;
    /** Selected subject id. */
    subjectId: number;
    /** Selected role ids. */
    roleIds: Array<number>;
    /** Owning user profile id. */
    userProfileId: number;
    /** Role ids that belong to the current user profile. */
    userProfileRoleIds: Array<number>;
    /** Selected center ids, or `null` before initialization. */
    centerIds: Array<number> | null;
    /** Selected farm ids. */
    farmIds: Array<number>;
}

/** Validation error map for the facility relation form. */
export type SubjectFacilityFormValuesErrors = Dictionary<keyof SubjectFacilityFormEntryData, string>;

/** Distinguishes breeder relations from organization relations. */
export type SubjectFacilityFormType = 'BREEDER' | 'ORGANIZATION';
